import { useEffect, useState } from "react";
import { getSelectUnitiesForDashboard } from "@/Api/unities";
import { getSelectSellers } from "@/Api/sellers";
import { DashboardFilters as FiltersType } from "@/types/dashboard";
import { Building2, User, Calendar, ChevronDown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { TypeUser } from "@/types/userType";

interface Unity {
  id: number;
  name: string;
}

interface Seller {
  id: number;
  user: {
    name: string;
  };
}

interface SellerInfo {
  id: number;
  name: string;
  unity_id: number;
}

interface DashboardFiltersProps {
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
}

export function DashboardFilters({
  filters,
  onFiltersChange,
}: DashboardFiltersProps) {
  const [unities, setUnities] = useState<Unity[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loadingUnities, setLoadingUnities] = useState(false);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [userTypeId, setUserTypeId] = useState<number | null>(null);
  const [restrictedMode, setRestrictedMode] = useState(false);
  const [sellerInfo, setSellerInfo] = useState<SellerInfo | null>(null);

  // Convert unity_id to array for multi-select
  const selectedUnityIds = Array.isArray(filters.unity_id)
    ? filters.unity_id.map(String)
    : filters.unity_id
      ? [String(filters.unity_id)]
      : [];

  // Convert seller_id to array for multi-select
  const selectedSellerIds = Array.isArray(filters.seller_id)
    ? filters.seller_id.map(String)
    : filters.seller_id
      ? [String(filters.seller_id)]
      : [];

  useEffect(() => {
    loadUnities();
  }, []);

  useEffect(() => {
    if (selectedUnityIds.length > 0 && !restrictedMode) {
      loadSellers(selectedUnityIds);
    } else if (!restrictedMode) {
      setSellers([]);
    }
  }, [JSON.stringify(selectedUnityIds), restrictedMode]);

  const loadUnities = async () => {
    setLoadingUnities(true);
    try {
      const response = await getSelectUnitiesForDashboard();
      if (response.data?.response) {
        const { unities: unitiesList, user_type_id, restricted_mode, seller_info } = response.data.response;

        setUnities(unitiesList || []);
        setUserTypeId(user_type_id);
        setRestrictedMode(restricted_mode || false);
        setSellerInfo(seller_info || null);

        // If user is a seller, auto-select their data
        if (user_type_id === TypeUser.seller && seller_info) {
          onFiltersChange({
            ...filters,
            unity_id: seller_info.unity_id,
            seller_id: seller_info.id,
          });
          // Set seller in the sellers list for display
          setSellers([{
            id: seller_info.id,
            user: { name: seller_info.name }
          }]);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    } finally {
      setLoadingUnities(false);
    }
  };

  const loadSellers = async (unityIds: string[]) => {
    setLoadingSellers(true);
    try {
      // Load sellers for all selected unities
      const allSellers: Seller[] = [];
      const seenIds = new Set<number>();

      for (const unityId of unityIds) {
        const response = await getSelectSellers({ unity_id: unityId, is_active: true });
        if (response.data?.response) {
          for (const seller of response.data.response) {
            if (!seenIds.has(seller.id)) {
              seenIds.add(seller.id);
              allSellers.push(seller);
            }
          }
        }
      }
      setSellers(allSellers);
    } catch (error) {
      console.error("Erro ao carregar vendedores:", error);
    } finally {
      setLoadingSellers(false);
    }
  };

  const handleUnityChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      unity_id: values.length === 0 ? undefined : values.map(Number),
      seller_id: undefined, // Reset sellers when unities change
    });
  };

  const handleSellerChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      seller_id: values.length === 0 ? undefined : values.map(Number),
    });
  };

  const toggleUnity = (unityId: string) => {
    if (selectedUnityIds.includes(unityId)) {
      handleUnityChange(selectedUnityIds.filter(id => id !== unityId));
    } else {
      handleUnityChange([...selectedUnityIds, unityId]);
    }
  };

  const toggleSeller = (sellerId: string) => {
    if (selectedSellerIds.includes(sellerId)) {
      handleSellerChange(selectedSellerIds.filter(id => id !== sellerId));
    } else {
      handleSellerChange([...selectedSellerIds, sellerId]);
    }
  };

  const toggleAllUnities = () => {
    if (selectedUnityIds.length === unities.length) {
      handleUnityChange([]);
    } else {
      handleUnityChange(unities.map(u => String(u.id)));
    }
  };

  const toggleAllSellers = () => {
    if (selectedSellerIds.length === sellers.length) {
      handleSellerChange([]);
    } else {
      handleSellerChange(sellers.map(s => String(s.id)));
    }
  };

  const handleGoalTypeChange = (type: "meta" | "super_meta") => {
    onFiltersChange({
      ...filters,
      goal_type: type,
    });
  };

  const handlePeriodChange = (period: string) => {
    if (period === "custom") {
      setIsDatePopoverOpen(true);
      return;
    }
    onFiltersChange({
      ...filters,
      period: period as FiltersType["period"],
      start_date: undefined,
      end_date: undefined,
    });
  };

  const handleApplyCustomDate = () => {
    if (customStartDate && customEndDate) {
      onFiltersChange({
        ...filters,
        period: "custom",
        start_date: customStartDate,
        end_date: customEndDate,
      });
      setIsDatePopoverOpen(false);
    }
  };

  const periods = [
    { key: "today", label: "Hoje" },
    { key: "yesterday", label: "Ontem" },
    { key: "week", label: "Semana" },
    { key: "month", label: "Mês" },
  ];

  const getCurrentPeriodLabel = () => {
    if (filters.period === "custom" && filters.start_date && filters.end_date) {
      return `${format(new Date(filters.start_date + "T00:00:00"), "dd/MM", { locale: ptBR })} - ${format(new Date(filters.end_date + "T00:00:00"), "dd/MM", { locale: ptBR })}`;
    }
    return periods.find(p => p.key === (filters.period || "month"))?.label || "Mês";
  };

  // Check if filter has selection (not "all")
  const hasUnitySelection = selectedUnityIds.length > 0 && selectedUnityIds.length < unities.length;
  const hasSellerSelection = selectedSellerIds.length > 0 && selectedSellerIds.length < sellers.length;

  // Determine if filters should be disabled
  const isSellerUser = userTypeId === TypeUser.seller;
  const filtersDisabled = restrictedMode && isSellerUser;

  // Get display text for seller filter when in restricted mode
  const getSellerDisplayText = () => {
    if (isSellerUser && sellerInfo) {
      return sellerInfo.name;
    }
    if (selectedUnityIds.length === 0) {
      return "Selecione unidade";
    }
    if (selectedSellerIds.length === 0) {
      return "Todos vendedores";
    }
    if (selectedSellerIds.length === sellers.length) {
      return "Todos vendedores";
    }
    return `${selectedSellerIds.length} vendedor${selectedSellerIds.length > 1 ? "es" : ""}`;
  };

  // Get display text for unity filter
  const getUnityDisplayText = () => {
    if (isSellerUser && unities.length === 1) {
      return unities[0]?.name || "Unidade";
    }
    if (selectedUnityIds.length === 0) {
      return "Todas unidades";
    }
    if (selectedUnityIds.length === unities.length) {
      return "Todas unidades";
    }
    return `${selectedUnityIds.length} unidade${selectedUnityIds.length > 1 ? "s" : ""}`;
  };

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
      {/* Unidade */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {filtersDisabled ? (
          // Seller view - show locked filter
          <div className="flex items-center min-w-[140px] sm:min-w-[160px] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm">
            <Lock className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0 text-gray-400" />
            <span className="truncate">{getUnityDisplayText()}</span>
          </div>
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex items-center justify-between min-w-[140px] sm:min-w-[160px] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-all",
                  hasUnitySelection
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
                disabled={loadingUnities}
              >
                <div className="flex items-center min-w-0">
                  <Building2 className="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">{getUnityDisplayText()}</span>
                </div>
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ml-1 sm:ml-2" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <div className="max-h-[300px] overflow-y-auto">
                <div
                  className="flex items-center space-x-2 px-3 py-2 border-b bg-gray-50 dark:bg-zinc-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700"
                  onClick={toggleAllUnities}
                >
                  <Checkbox
                    checked={selectedUnityIds.length === unities.length && unities.length > 0}
                    className={cn(
                      selectedUnityIds.length > 0 && selectedUnityIds.length < unities.length &&
                      "data-[state=unchecked]:bg-primary/50 data-[state=unchecked]:border-primary"
                    )}
                  />
                  <span className="text-sm font-medium">Todas as unidades</span>
                </div>
                {unities.map((unity) => {
                  const isSelected = selectedUnityIds.includes(String(unity.id));
                  return (
                    <div
                      key={unity.id}
                      className={cn(
                        "flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700",
                        isSelected && "bg-primary/5"
                      )}
                      onClick={() => toggleUnity(String(unity.id))}
                    >
                      <Checkbox checked={isSelected} />
                      <span className="text-sm">{unity.name}</span>
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Vendedor */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {filtersDisabled ? (
          // Seller view - show locked filter
          <div className="flex items-center min-w-[140px] sm:min-w-[160px] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm">
            <Lock className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0 text-gray-400" />
            <span className="truncate">{getSellerDisplayText()}</span>
          </div>
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex items-center justify-between min-w-[140px] sm:min-w-[160px] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-all",
                  hasSellerSelection
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200",
                  (selectedUnityIds.length === 0 || loadingSellers) && "opacity-50 cursor-not-allowed"
                )}
                disabled={selectedUnityIds.length === 0 || loadingSellers}
              >
                <div className="flex items-center min-w-0">
                  <User className="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">{getSellerDisplayText()}</span>
                </div>
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ml-1 sm:ml-2" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <div className="max-h-[300px] overflow-y-auto">
                <div
                  className="flex items-center space-x-2 px-3 py-2 border-b bg-gray-50 dark:bg-zinc-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700"
                  onClick={toggleAllSellers}
                >
                  <Checkbox
                    checked={selectedSellerIds.length === sellers.length && sellers.length > 0}
                    className={cn(
                      selectedSellerIds.length > 0 && selectedSellerIds.length < sellers.length &&
                      "data-[state=unchecked]:bg-primary/50 data-[state=unchecked]:border-primary"
                    )}
                  />
                  <span className="text-sm font-medium">Todos os vendedores</span>
                </div>
                {sellers.map((seller) => {
                  const isSelected = selectedSellerIds.includes(String(seller.id));
                  return (
                    <div
                      key={seller.id}
                      className={cn(
                        "flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700",
                        isSelected && "bg-primary/5"
                      )}
                      onClick={() => toggleSeller(String(seller.id))}
                    >
                      <Checkbox checked={isSelected} />
                      <span className="text-sm">{seller.user?.name}</span>
                    </div>
                  );
                })}
                {sellers.length === 0 && (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    Nenhum vendedor disponível
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Período */}
      <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-9 sm:h-10 gap-1.5 sm:gap-2 px-2 sm:px-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 text-xs sm:text-sm"
          >
            <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
            {getCurrentPeriodLabel()}
            <ChevronDown className="h-3 w-3 text-gray-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="start">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {periods.map((p) => (
                <Button
                  key={p.key}
                  variant={filters.period === p.key ? "default" : "outline"}
                  size="sm"
                  className="h-9"
                  onClick={() => {
                    handlePeriodChange(p.key);
                    setIsDatePopoverOpen(false);
                  }}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Período personalizado</p>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-[130px] h-9"
                />
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-[130px] h-9"
                />
              </div>
              <Button
                className="w-full mt-3 h-9"
                size="sm"
                onClick={handleApplyCustomDate}
                disabled={!customStartDate || !customEndDate}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Separador */}
      <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 hidden lg:block" />

      {/* Meta / Super Meta */}
      <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 sm:ml-auto">
        <button
          onClick={() => handleGoalTypeChange("meta")}
          className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all ${
            filters.goal_type === "meta"
              ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          Meta
        </button>
        <button
          onClick={() => handleGoalTypeChange("super_meta")}
          className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all ${
            filters.goal_type === "super_meta"
              ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          Super Meta
        </button>
      </div>
    </div>
  );
}
